import React from "react";
import {CustomSwitch} from "../../../../../../components/CustomSwitch";
import {CustomCheckbox} from "../../../../../../components/CustomCheckbox";
import {CustomTextField} from "../../../../../../components/CustomTextField";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @description
 * Renders the complementary section of a movement: the departure of the vehicle
 * for an entry and its arrival for an exit. The labels and the model fields
 * written by the section come from its configuration.
 *
 * @param {Function} t - Translation function.
 * @param {Object} section - Section configuration of the selected movement direction.
 * @param {Object} materialRequest - Current form information.
 * @param {Function} handleChange - Change handler of the form.
 * @param {boolean} emptyFields - Indicates whether the required fields must be highlighted.
 * @param {boolean} showCheckbox - Indicates whether the vehicle can be declared empty. The forms
 * that do not offer it always capture the details of the movement.
 */
const DepartureSection = ({
    t,
    section,
    materialRequest,
    handleChange,
    emptyFields,
    showCheckbox = true
}) => {

    const isRequired = materialRequest?.[section.requiredName];
    const isChecked = showCheckbox && materialRequest?.[section.checkboxName];
    const showDetails = isRequired && !isChecked;
    const SectionIcon = section.Icon;

    /**
     * Forwards the change of a switch or a checkbox to the form handler.
     *
     * MUI calls onChange with (event, checked) and the second argument is read
     * by the form handler as the name of the field, so only the value of the
     * event is sent.
     *
     * @param {Object} e - Change event of the switch or the checkbox.
     *
     * @returns {void}
     */
    const handleCheckedChange = (e) => {
        handleChange({ target: { name: e.target.name, value: e.target.checked } });
    };

    /**
     * Forwards the change of the checkbox and cleans the details field when it
     * is checked, because the field is hidden and its value is no longer valid.
     *
     * @param {Object} e - Change event of the checkbox.
     *
     * @returns {void}
     */
    const handleCheckboxChange = (e) => {
        handleCheckedChange(e);

        if (e.target.checked)
            handleChange({ target: { name: section.detailsName, value: '' } });
    };

    return (
        <div className={"row"}>
            <div className={"border-radius-8px border-full-shades-300 px-0"}>
                <div className={"d-flex p-2"}>
                    <div className={"mr-2"}><SectionIcon/></div>
                    <p className={"fs-14px ibl-font-w-600"}>{t(section.sectionTitle)}</p>
                </div>
                <div className={"border-bottom-shades-300 mb-3"}/>
                <div className={"border-radius-8px background-shades-50 m-3 p-3"}>
                    <div className={"d-flex justify-content-between mb-3"}>
                        <div>
                            <p className={"fs-14px ibl-font-w-600 mb-2"}>{t(section.title)}</p>
                            <p className={"fs-14px"}>{t(section.description)}</p>
                        </div>

                        <CustomSwitch
                            id={`cs_${section.idPrefix}_required`}
                            name={section.requiredName}
                            checked={isRequired}
                            showBorder={false}
                            onChange={handleCheckedChange}
                        />
                    </div>

                    { isRequired &&
                        <>
                            { showCheckbox &&
                                <CustomCheckbox
                                    id={`cb_${section.idPrefix}_vehicle_empty`}
                                    trans={section.checkboxTrans}
                                    name={section.checkboxName}
                                    checked={isChecked}
                                    onChange={handleCheckboxChange}
                                    disableRipple={true}
                                    marginbutton={true}
                                />
                            }

                            { showDetails &&
                                <CustomTextField
                                    id={`txt_${section.idPrefix}_details`}
                                    length={512}
                                    name={section.detailsName}
                                    value={materialRequest?.[section.detailsName]}
                                    type='text'
                                    multiline={true}
                                    required={showDetails}
                                    onChange={handleChange}
                                    emptyFields={emptyFields && showDetails}
                                    placeholder={t(section.placeholder)}
                                />
                            }
                        </>
                    }
                </div>
            </div>
        </div>
    );
};

export default DepartureSection;
